import React, { useState } from "react";
import { getSupabase } from "@/lib/supabaseClient";
import { i18n } from "@/lib/api";
import { GlassesIcon } from "./GlassesIcon";

export default function AuthScreen({
  language,
  onLanguageChange,
  onAuthenticated,
  configured,
  bootstrapError,
  bootstrapDiagnostics,
  initialTab,
  onBack,
}) {
  const t = i18n[language];
  const [tab, setTab] = useState(initialTab === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  if (!configured) {
    const d = bootstrapDiagnostics || {};
    return (
      <div style={{ maxWidth: 760, margin: "5rem auto", padding: "1rem 1.5rem" }} data-testid="auth-not-configured">
        <h1 className="h-display" style={{ fontSize: "1.6rem" }}>
          POCKET TA AI
        </h1>
        <div className="line" />
        <p className="txt-mute">
          Authentication is awaiting configuration. The backend must supply
          <span className="mono"> SUPABASE_URL</span> and
          <span className="mono"> SUPABASE_ANON_KEY</span> in the BACKEND
          environment variables (not the frontend section). Once set and the
          backend is redeployed, the login surface will activate automatically -
          no frontend rebuild is required because configuration is fetched at
          runtime.
        </p>
        <div className="line" />
        <div className="h-eyebrow">Bootstrap diagnostics</div>
        <pre className="mono" style={{ fontSize: "0.82rem", color: "var(--ink-mute)", whiteSpace: "pre-wrap" }} data-testid="auth-config-diagnostics">
{`Backend endpoint           = ${process.env.REACT_APP_BACKEND_URL || "EMPTY"}/api/public/config
Frontend bootstrap error   = ${bootstrapError || "(none)"}
Backend URL present        = ${d.url_present === true ? "YES" : d.url_present === false ? "NO" : "(unknown)"}
Backend URL length         = ${d.url_length ?? "(unknown)"}
Backend anon key present   = ${d.anon_key_present === true ? "YES" : d.anon_key_present === false ? "NO" : "(unknown)"}
Backend anon key length    = ${d.anon_key_length ?? "(unknown)"}
NODE_ENV                   = ${process.env.NODE_ENV || "unknown"}`}
        </pre>
        <div className="line" />
        <div className="h-eyebrow">Fix this in your hosting dashboard</div>
        <p className="txt-mute" style={{ fontSize: "0.9rem" }}>
          Open the BACKEND environment section of your Emergent deployment and set both:
        </p>
        <pre className="mono" style={{ fontSize: "0.82rem", color: "var(--ink)" }}>
{`SUPABASE_URL=https://<your-project-ref>.supabase.co
SUPABASE_ANON_KEY=sb_publishable_xxxxxxxxxxxxxxxx`}
        </pre>
        <p className="txt-mute" style={{ fontSize: "0.9rem" }}>
          Use the SAME values you put in the frontend section earlier (without the
          REACT_APP_ prefix). Then redeploy the backend and reload this page in
          an incognito window.
        </p>
      </div>
    );
  }

  const reset = () => {
    setError("");
    setInfo("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;
      onAuthenticated();
    } catch (err) {
      setError(err.message || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName, preferred_language: language },
        },
      });
      if (err) throw err;
      setInfo(
        language === "es"
          ? "Cuenta creada. Revisa tu correo para confirmar (si esta habilitado) e inicia sesion."
          : "Account created. Check your email to confirm (if required) and then log in.",
      );
      setTab("login");
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    reset();
    if (!email) {
      setError(language === "es" ? "Ingresa tu correo primero." : "Enter your email first.");
      return;
    }
    setBusy(true);
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      });
      if (err) throw err;
      setInfo(language === "es" ? "Correo de recuperacion enviado." : "Recovery email sent.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: "3rem 1.5rem 4rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <GlassesIcon size={48} />
        </div>
        <div className="lang-toggle" data-testid="auth-lang-toggle">
          <button
            data-testid="auth-lang-en"
            className={language === "en" ? "active" : ""}
            onClick={() => onLanguageChange("en")}
          >
            EN
          </button>
          <button
            data-testid="auth-lang-es"
            className={language === "es" ? "active" : ""}
            onClick={() => onLanguageChange("es")}
          >
            ES
          </button>
        </div>
      </div>

      <div className="rise rise-1">
        <div className="h-eyebrow">{t.tagline}</div>
        <h1 className="h-display" style={{ fontSize: "2.2rem", marginTop: "0.45rem" }}>
          POCKET TA AI
        </h1>
      </div>

      <div className="line-strong rise rise-2" />

      <div className="rise rise-3" style={{ display: "flex", gap: "2rem", marginBottom: "1.75rem" }}>
        <button
          data-testid="auth-tab-login"
          className="btn-link"
          onClick={() => {
            setTab("login");
            reset();
          }}
          style={{ color: tab === "login" ? "var(--ink)" : "var(--ink-mute)", borderBottomColor: tab === "login" ? "var(--ink)" : "transparent" }}
        >
          {t.login}
        </button>
        <button
          data-testid="auth-tab-register"
          className="btn-link"
          onClick={() => {
            setTab("register");
            reset();
          }}
          style={{ color: tab === "register" ? "var(--ink)" : "var(--ink-mute)", borderBottomColor: tab === "register" ? "var(--ink)" : "transparent" }}
        >
          {t.register}
        </button>
      </div>

      <form onSubmit={tab === "login" ? handleLogin : handleRegister} className="rise rise-4">
        {tab === "register" && (
          <div className="input-line focus-line" style={{ marginBottom: "1.25rem" }}>
            <label className="h-eyebrow" htmlFor="auth-name">Full name</label>
            <input
              id="auth-name"
              data-testid="auth-name-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Dr. Jane Researcher"
              autoComplete="name"
            />
          </div>
        )}

        <div className="input-line focus-line" style={{ marginBottom: "1.25rem" }}>
          <label className="h-eyebrow" htmlFor="auth-email">{t.email}</label>
          <input
            id="auth-email"
            data-testid="auth-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div className="input-line focus-line" style={{ marginBottom: "1.5rem", position: "relative" }}>
          <label className="h-eyebrow" htmlFor="auth-password">{t.password}</label>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input
              id="auth-password"
              data-testid="auth-password-input"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={tab === "login" ? "current-password" : "new-password"}
              required
              minLength={6}
            />
            <button
              type="button"
              data-testid="auth-toggle-password"
              className="btn-link"
              style={{ fontSize: "0.78rem", letterSpacing: "0.08em", textTransform: "uppercase" }}
              onClick={() => setShowPw(!showPw)}
              aria-label={showPw ? t.hidePassword : t.showPassword}
              title={showPw ? t.hidePassword : t.showPassword}
            >
              {showPw ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {error && (
          <div data-testid="auth-error" className="txt-danger" style={{ marginBottom: "0.85rem", fontSize: "0.92rem" }}>
            {error}
          </div>
        )}
        {info && (
          <div data-testid="auth-info" className="txt-mute" style={{ marginBottom: "0.85rem", fontSize: "0.92rem" }}>
            {info}
          </div>
        )}

        <button data-testid="auth-submit" type="submit" disabled={busy} className="btn-primary" style={{ width: "100%" }}>
          {busy ? "..." : tab === "login" ? t.login : t.register}
        </button>
      </form>

      <div className="rise rise-5" style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <button data-testid="auth-forgot-password" className="btn-link" onClick={handleForgotPassword}>
          {t.forgotPassword}
        </button>
        <button data-testid="auth-forgot-userid" className="btn-link" onClick={handleForgotPassword}>
          {t.forgotUserId}
        </button>
        {onBack && (
          <button
            data-testid="auth-back-to-welcome"
            className="btn-link"
            onClick={onBack}
            style={{ color: "var(--ink-mute)", marginTop: "0.5rem" }}
          >
            {language === "es" ? "Volver a la pagina principal" : "Back to welcome"}
          </button>
        )}
      </div>
    </div>
  );
}
